import { useContext, useState } from 'react'
import { analyticsContext, AllowedEvents } from '@opallabs/quanta'
import { getContentDragData, ContentDragData } from '../dragData'
import type { OnDropOnCard, Moment } from './momentCardTypes'

type UseMomentDragArgs = {
  isContentLoading: boolean
  isMomentDragActive: boolean
  moment?: Moment
}

type UseMomentDragValues = {
  dragCount: number
  onDragEnter: (e: React.DragEvent<HTMLDivElement>) => void
  onDragLeave: () => void
  onDropOnCard: OnDropOnCard
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void
}

export const useMomentDragEvents = ({
  isContentLoading,
  isMomentDragActive,
  moment,
}: UseMomentDragArgs): UseMomentDragValues => {
  const [dragCount, setDragCount] = useState(0)
  const { track } = useContext(analyticsContext)
  if (!moment)
    return {
      dragCount,
      onDragEnter: () => null,
      onDragLeave: () => null,
      onDropOnCard: () => null,
      onDragOver: () => null,
    }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const onDropOnCard: OnDropOnCard = ({
    e,
    onMoveContent,
    onReorderContent,
  }) => {
    e.preventDefault()

    if (isContentLoading || moment.posts.length !== moment.postCount) return

    const {
      contentId,
      contentPosition: originContentPosition,
      momentId: originMomentId,
    } = getContentDragData(e) as ContentDragData

    if (!contentId) return

    const destinationMomentId = moment.id

    const sameMoment = originMomentId === destinationMomentId
    track(AllowedEvents.drop, {
      id: contentId,
      dragIntent: sameMoment ? 'reordered' : 'moved',
      type: 'content',
    })
    setDragCount(0)

    if (!sameMoment) {
      onMoveContent?.({
        originMomentId,
        destinationMomentId: destinationMomentId,
        contentId,
        newContentPosition: 0,
      })
    } else {
      onReorderContent?.({
        destinationIndex: 0,
        moment,
        originIndex: originContentPosition,
      })
    }
  }

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (isContentLoading || isMomentDragActive) return
    setDragCount(dragCount + 1)
    e.dataTransfer.dropEffect = 'move'
  }

  const onDragLeave = () => {
    if (isContentLoading || isMomentDragActive) return
    setDragCount(dragCount - 1)
  }

  return {
    dragCount,
    onDragEnter,
    onDragLeave,
    onDropOnCard,
    onDragOver,
  }
}
